import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUsername } from "@/hooks/useUsername";
import { useAffiliateStatus } from "@/hooks/useAffiliateStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/affiliate/StatCard";
import { CommissionsTable } from "@/components/affiliate/CommissionsTable";
import { TaxInformationCard } from "@/components/affiliate/TaxInformationCard";
import { PayoutsCard } from "@/components/affiliate/PayoutsCard";
import { SeoHead } from "@/components/SeoHead";
import { daysBetween, formatCommissionAmount } from "@/lib/affiliateDashboard";
import { useAffiliateDashboardData } from "./affiliate/useAffiliateDashboardData";
import { ReferralLinkCard } from "./affiliate/ReferralLinkCard";
import { PayoutHistoryCard } from "./affiliate/PayoutHistoryCard";

export default function Affiliate() {
  const { user } = useAuth();
  const { username, loading: usernameLoading } = useUsername();
  const { status, loading: statusLoading, refresh } = useAffiliateStatus();
  const profileId = status?.profile_id ?? null;

  const { stats, rows, payouts, loadingData, holdbackDays, lapseGraceDays, enrollment } =
    useAffiliateDashboardData(profileId, user?.id);

  const [enrolling, setEnrolling] = useState(false);

  const enroll = async () => {
    setEnrolling(true);
    const args: { _profile_id?: string } = {};
    if (profileId) args._profile_id = profileId;
    const { error } = await supabase.rpc("enroll_in_affiliate_program", args);
    setEnrolling(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Enrolled in the affiliate program");
    await refresh();
  };

  const primaryStats = stats[0] ?? {
    currency: "USD",
    pending_cents: 0,
    approved_cents: 0,
    paid_cents: 0,
    forfeited_cents: 0,
    reversed_cents: 0,
  };

  const isEligible = status?.eligible_now ?? false;
  const lapsedAt = enrollment?.eligibility_lapsed_at ?? null;
  const lapseDaysElapsed = lapsedAt ? daysBetween(new Date(lapsedAt), new Date()) : 0;
  const lapseDaysRemaining = Math.max(0, lapseGraceDays - lapseDaysElapsed);
  const showLapseBanner = status?.is_enrolled && !isEligible && lapsedAt;

  if (statusLoading || usernameLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!status?.is_enrolled) {
    return (
      <div className="mx-auto max-w-3xl space-y-6 p-4">
        <header>
          <h1 className="font-serif text-3xl font-semibold">Affiliate program</h1>
        </header>
        <Card className="shadow-soft">
          <CardContent className="space-y-4 p-6">
            {!status?.has_ever_paid && !status?.override_eligible ? (
              <p className="text-sm text-muted-foreground">
                The affiliate program unlocks after your first successful subscription
                payment. Once active, you'll earn 15% of every paid invoice from people
                you refer.
              </p>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
                  <div className="space-y-1">
                    <p className="font-medium">You're eligible</p>
                    <p className="text-sm text-muted-foreground">
                      Enroll to claim your referral link. You'll earn 15% on every paid
                      invoice from people you refer, for as long as your subscription
                      stays active.
                    </p>
                  </div>
                </div>
                <Button onClick={enroll} disabled={enrolling}>
                  {enrolling ? "Enrolling…" : "Enroll in affiliate program"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4">
      <SeoHead
        title="Affiliate dashboard — Excerptus"
        description="Manage your Excerptus affiliate referrals, commissions, and payouts."
        noindex
      />
      <header className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-serif text-3xl font-semibold">Affiliate dashboard</h1>
          {status?.is_super_affiliate ? (
            <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Super Affiliate
            </span>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {status?.is_super_affiliate
            ? `Showing activity for @${username ?? "this Persona"} — tracked separately and excluded from program KPIs. Switch Persona to see other links.`
            : `Showing activity for @${username ?? "this Persona"}. Earn 15% on every paid invoice from people who sign up through your link. Switch Persona to see other links.`}
        </p>
      </header>

      {showLapseBanner ? (
        <Card className="border-destructive/40 bg-destructive/5 shadow-soft">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
            <div className="flex-1 space-y-1 text-sm">
              <p className="font-medium">
                Subscription lapsed {lapseDaysElapsed} {lapseDaysElapsed === 1 ? "day" : "days"} ago
              </p>
              <p className="text-muted-foreground">
                Reactivate within {lapseDaysRemaining}{" "}
                {lapseDaysRemaining === 1 ? "day" : "days"} or your pending commissions
                will be forfeited. Approved commissions are safe.
              </p>
            </div>
            <Button asChild size="sm" variant="outline">
              <Link to="/pricing/">
                Reactivate <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <ReferralLinkCard username={username} userId={user?.id} />

      <TaxInformationCard />

      <PayoutsCard userId={user?.id} />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label="Pending"
          value={formatCommissionAmount(primaryStats.pending_cents, primaryStats.currency)}
          hint={`Approves after ${holdbackDays}-day holdback`}
          loading={loadingData}
        />
        <StatCard
          label="Approved"
          value={formatCommissionAmount(primaryStats.approved_cents, primaryStats.currency)}
          hint="Ready for next payout"
          loading={loadingData}
        />
        <StatCard
          label="Lifetime paid"
          value={formatCommissionAmount(primaryStats.paid_cents, primaryStats.currency)}
          hint="All-time disbursed"
          loading={loadingData}
        />
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">Commissions</CardTitle>
        </CardHeader>
        <CardContent>
          {!loadingData && rows.length === 0 ? (
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                No commissions yet. Share your referral link to get started — every paid
                invoice earns you 15%, indefinitely.
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Drop it in your newsletter footer or about page</li>
                <li>Pin it to your profile in communities you're active in</li>
                <li>Mention it when you recommend Excerptus</li>
              </ul>
            </div>
          ) : (
            <CommissionsTable
              rows={rows}
              holdbackDays={holdbackDays}
              loading={loadingData}
            />
          )}
        </CardContent>
      </Card>

      <PayoutHistoryCard payouts={payouts} />
    </div>
  );
}
